import React, { useState } from 'react';
import { Table, Button, Card, Space, Tag, Modal, Form, Input, Switch, message, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { FormDto } from '../../api/forms';
import { formsApi } from '../../api/forms';
import { useNavigate } from 'react-router-dom';

export const Forms: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingForm, setEditingForm] = useState<FormDto | null>(null);
    const [form] = Form.useForm();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data, isLoading } = useQuery({
        queryKey: ['adminForms', page, pageSize],
        queryFn: () => formsApi.getAdminForms({ pageNumber: page, pageSize: pageSize })
    });

    const createMutation = useMutation({
        mutationFn: formsApi.createForm,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminForms'] });
            message.success('Form created successfully');
            setIsModalOpen(false);
            form.resetFields();
        }
    });

    const updateMutation = useMutation({
        mutationFn: (vars: { id: string, data: Partial<FormDto> }) => formsApi.updateForm(vars.id, vars.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminForms'] });
            message.success('Form updated successfully');
            setIsModalOpen(false);
            setEditingForm(null);
            form.resetFields();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: formsApi.deleteForm,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminForms'] });
            message.success('Form deleted successfully');
        }
    });

    const handleSubmit = async (values: any) => {
        if (editingForm) {
            updateMutation.mutate({ id: editingForm.id, data: values });
        } else {
            // New forms start with empty fields, then you edit them in builder
            createMutation.mutate({ ...values, fields: [] });
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Slug',
            dataIndex: 'slug',
            key: 'slug',
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: FormDto) => (
                <Space orientation="horizontal">
                    <Tooltip title="View Submissions">
                        <Button 
                            icon={<FileTextOutlined />} 
                            onClick={() => navigate(`/admin/forms/${record.id}/submissions`)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit Structure">
                        <Button 
                            icon={<EditOutlined />} 
                            onClick={() => {
                                setEditingForm(record);
                                form.setFieldsValue(record);
                                setIsModalOpen(true);
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Form Builder">
                        <Button 
                            type="primary"
                            icon={<PlusOutlined />} 
                            onClick={() => navigate(`/admin/forms/${record.id}/builder`)}
                        >
                            Builder
                        </Button>
                    </Tooltip>
                    <Button 
                        danger 
                        icon={<DeleteOutlined />} 
                        onClick={() => {
                            Modal.confirm({
                                title: 'Are you sure?',
                                content: 'This will delete the form and all its submissions.',
                                onOk: () => deleteMutation.mutate(record.id)
                            });
                        }}
                    />
                </Space>
            ),
        },
    ];

    return (
        <Card 
            title="Forms Management" 
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                    setEditingForm(null);
                    form.resetFields();
                    setIsModalOpen(true);
                }}>
                    Create Form
                </Button>
            }
        >
            <Table 
                dataSource={data?.data || []} 
                columns={columns} 
                loading={isLoading}
                rowKey="id"
                rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: data?.totalRecords || 0,
                    onChange: (p, s) => {
                        setPage(p);
                        setPageSize(s);
                    },
                    showSizeChanger: true
                }}
            />

            <Modal
                title={editingForm ? 'Edit Form Basic Info' : 'Create New Form'}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={() => form.submit()}
                confirmLoading={createMutation.isPending || updateMutation.isPending}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                        <Input placeholder="e.g., Course Registration" />
                    </Form.Item>
                    <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
                        <Input placeholder="e.g., course-registration" />
                    </Form.Item>
                    <Form.Item name="isActive" label="Is Active" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};
